import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Image
} from 'react-bootstrap';
import axios from 'axios';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const TestimonialsManager = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState({ avg_rating: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    content: '',
    image_url: '',
    rating: 5,
    display_order: 0,
    is_active: true
  });

  const API_BASE_URL = BASE_URL;
  
  useEffect(() => {
    // Fetch user data from localStorage or API
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    
    fetchTestimonials();
    fetchStats();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      designation: '',
      content: '',
      image_url: '',
      rating: 5,
      display_order: 0,
      is_active: true
    });
    setImagePreview(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/admin/login';
  };

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/testimonials.php?admin=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const processedTestimonials = response.data.data.map(testimonial => ({
          ...testimonial,
          id: parseInt(testimonial.id),
          rating: parseInt(testimonial.rating),
          display_order: parseInt(testimonial.display_order),
          is_active: testimonial.is_active === "1" || testimonial.is_active === 1
        })).sort((a, b) => {
          if (a.display_order === b.display_order) {
            return new Date(b.created_at) - new Date(a.created_at);
          }
          return a.display_order - b.display_order;
        });
        
        setTestimonials(processedTestimonials);
      } else {
        setError('Failed to load testimonials');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/testimonials.php?stats=true`);
      
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) : value)
    }));
  };

  const handleTextareaChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Set file for upload
      setFormData(prev => ({
        ...prev,
        image_file: file
      }));
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({
      ...prev,
      image_url: '',
      image_file: null
    }));
  };

  const uploadImage = async (file) => {
    try {
      setUploadingImage(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await axios.post(`${API_BASE_URL}/upload_testimonial_image.php`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        return response.data.image_url;
      }
      return null;
    } catch (err) {
      console.error('Image upload error:', err);
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <i 
        key={index} 
        className={`fas fa-star ${index < rating ? 'text-warning' : 'text-muted'}`}
      ></i>
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let dataToSend = { ...formData };
      
      // Remove image_file from dataToSend if it exists
      if (dataToSend.image_file) {
        delete dataToSend.image_file;
      }
      
      // If there's a new image to upload
      if (formData.image_file) {
        const uploadedUrl = await uploadImage(formData.image_file);
        if (uploadedUrl) {
          dataToSend.image_url = uploadedUrl;
        } else {
          setError('Failed to upload image');
          return;
        }
      }
      
      // If order is not specified, set to next available
      if (!dataToSend.display_order || dataToSend.display_order <= 0) {
        const maxOrder = testimonials.length > 0 
          ? Math.max(...testimonials.map(testimonial => parseInt(testimonial.display_order))) 
          : 0;
        dataToSend.display_order = maxOrder + 1;
      }
      
      const token = localStorage.getItem('token');
      let response;
      
      if (editingTestimonial) {
        response = await axios.put(
          `${API_BASE_URL}/testimonials.php?id=${editingTestimonial.id}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/testimonials.php`, dataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      if (response.data.success) {
        setSuccess(editingTestimonial ? 'Testimonial updated!' : 'Testimonial added!');
        setShowModal(false);
        resetForm();
        setEditingTestimonial(null);
        fetchTestimonials();
        fetchStats();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Operation failed: ' + (response.data.message || ''));
      }
    } catch (err) {
      setError('Error saving testimonial: ' + (err.response?.data?.message || err.message));
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API_BASE_URL}/testimonials.php?id=${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          setSuccess('Testimonial deleted!');
          fetchTestimonials();
          fetchStats();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete testimonial: ' + (response.data.message || ''));
        }
      } catch (err) {
        setError('Error deleting testimonial');
        console.error(err);
      }
    }
  };

  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      name: testimonial.name,
      designation: testimonial.designation || '',
      content: testimonial.content || '',
      image_url: testimonial.image_url || '',
      rating: parseInt(testimonial.rating),
      display_order: parseInt(testimonial.display_order),
      is_active: testimonial.is_active === true || testimonial.is_active === "1" || testimonial.is_active === 1
    });
    
    // Set image preview if image_url exists
    if (testimonial.image_url) {
      setImagePreview(testimonial.image_url);
    }
    
    setShowModal(true);
  };

  const handleReorder = async (direction, testimonial) => {
    try {
      const currentTestimonials = [...testimonials];
      const currentIndex = currentTestimonials.findIndex(item => item.id === testimonial.id);
      
      const token = localStorage.getItem('token');
      
      if (direction === 'up' && currentIndex > 0) {
        const prevTestimonial = currentTestimonials[currentIndex - 1];
        
        await axios.put(`${API_BASE_URL}/testimonials.php?id=${testimonial.id}`, {
          ...testimonial,
          display_order: prevTestimonial.display_order,
          is_active: (testimonial.is_active === true || testimonial.is_active === "1" || testimonial.is_active === 1) ? 1 : 0
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        await axios.put(`${API_BASE_URL}/testimonials.php?id=${prevTestimonial.id}`, {
          ...prevTestimonial,
          display_order: testimonial.display_order,
          is_active: (prevTestimonial.is_active === true || prevTestimonial.is_active === "1" || prevTestimonial.is_active === 1) ? 1 : 0
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        setSuccess('Order updated!');
        setTimeout(() => setSuccess(''), 3000);
        
      } else if (direction === 'down' && currentIndex < currentTestimonials.length - 1) {
        const nextTestimonial = currentTestimonials[currentIndex + 1];
        
        await axios.put(`${API_BASE_URL}/testimonials.php?id=${testimonial.id}`, {
          ...testimonial,
          display_order: nextTestimonial.display_order,
          is_active: (testimonial.is_active === true || testimonial.is_active === "1" || testimonial.is_active === 1) ? 1 : 0
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        await axios.put(`${API_BASE_URL}/testimonials.php?id=${nextTestimonial.id}`, {
          ...nextTestimonial,
          display_order: testimonial.display_order,
          is_active: (nextTestimonial.is_active === true || nextTestimonial.is_active === "1" || nextTestimonial.is_active === 1) ? 1 : 0
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        setSuccess('Order updated!');
        setTimeout(() => setSuccess(''), 3000);
      }
      
      fetchTestimonials();
      
    } catch (err) {
      console.error('Error reordering:', err);
      setError('Failed to reorder: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleToggleActive = async (testimonial) => {
    try {
      const currentActive = testimonial.is_active === true || testimonial.is_active === "1" || testimonial.is_active === 1;
      const newActiveStatus = !currentActive;
      
      const updatedData = {
        name: testimonial.name,
        designation: testimonial.designation || '',
        content: testimonial.content || '',
        image_url: testimonial.image_url || '',
        rating: parseInt(testimonial.rating),
        display_order: parseInt(testimonial.display_order),
        is_active: newActiveStatus ? 1 : 0
      };
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/testimonials.php?id=${testimonial.id}`,
        updatedData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        fetchTestimonials();
        fetchStats();
      } else {
        setError('Failed to update status: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Error toggling active status:', err);
      setError('Failed to update status');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTestimonial(null);
    resetForm();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'No content';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading testimonials...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Testimonials Manager</h2>
            <p className="text-muted">Manage customer testimonials and reviews</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => {
              const nextOrder = testimonials.length > 0 
                ? Math.max(...testimonials.map(testimonial => parseInt(testimonial.display_order))) + 1 
                : 1;
              setFormData(prev => ({ 
                ...prev, 
                display_order: nextOrder,
                is_active: true
              }));
              setShowModal(true);
            }}>
              <i className="fas fa-plus me-2"></i>Add Testimonial
            </Button>
          </Col>
        </Row>

        {/* Statistics Card */}
        <Row className="mb-4">
          <Col md={6} lg={3}>
            <Card className="border-primary">
              <Card.Body className="text-center">
                <h2 className="display-4 text-primary">{testimonials.length}</h2>
                <p className="text-muted mb-0">Total Testimonials</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} lg={3}>
            <Card className="border-warning">
              <Card.Body className="text-center">
                {statsLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <h2 className="display-4 text-warning">{stats.avg_rating}/5</h2>
                    <div className="mb-2">
                      {renderStars(stats.avg_rating)}
                    </div>
                    <p className="text-muted mb-0">Average Rating</p>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} lg={3}>
            <Card className="border-success">
              <Card.Body className="text-center">
                <h2 className="display-4 text-success">
                  {testimonials.filter(t => t.is_active === true || t.is_active === "1" || t.is_active === 1).length}
                </h2>
                <p className="text-muted mb-0">Active Testimonials</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} lg={3}>
            <Card className="border-info">
              <Card.Body className="text-center">
                <h2 className="display-4 text-info">
                  {testimonials.filter(t => t.image_url).length}
                </h2>
                <p className="text-muted mb-0">With Photos</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th width="80">Photo</th>
                  <th>Name & Designation</th>
                  <th>Content</th>
                  <th>Rating</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th width="150">Actions</th>
                </tr>
              </thead>
              <tbody>
                {testimonials.map((testimonial, index) => {
                  const serialNumber = index + 1;
                  
                  return (
                    <tr key={testimonial.id}>
                      <td>
                        {testimonial.image_url ? (
                          <Image 
                            src={`${UPLOAD_BASE_URL}${testimonial.image_url}`} 
                            alt={testimonial.name}
                            roundedCircle
                            thumbnail 
                            style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                          />
                        ) : (
                          <div 
                            className="rounded-circle bg-secondary d-flex align-items-center justify-content-center"
                            style={{ width: '60px', height: '60px' }}
                          >
                            <i className="fas fa-user text-white"></i>
                          </div>
                        )}
                      </td>
                      <td>
                        <strong>{testimonial.name}</strong>
                        {testimonial.designation && (
                          <small className="d-block text-muted">
                            {testimonial.designation}
                          </small>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">
                          {truncateText(testimonial.content, 60)}
                        </small>
                      </td>
                      <td>
                        <div className="text-warning">
                          {renderStars(testimonial.rating)}
                          <div className="small text-muted mt-1">
                            {testimonial.rating} stars
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => handleReorder('up', testimonial)}
                            disabled={parseInt(testimonial.display_order) <= 1}
                          >
                            <i className="fas fa-arrow-up"></i>
                          </Button>
                          <span className="mx-2">{serialNumber}</span>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => handleReorder('down', testimonial)}
                            disabled={parseInt(testimonial.display_order) >= testimonials.length}
                          >
                            <i className="fas fa-arrow-down"></i>
                          </Button>
                        </div>
                      </td>
                      <td>
                        <Form.Check
                          type="switch"
                          checked={testimonial.is_active === true || testimonial.is_active === "1" || testimonial.is_active === 1}
                          onChange={() => handleToggleActive(testimonial)}
                          label={(testimonial.is_active === true || testimonial.is_active === "1" || testimonial.is_active === 1) ? 'Active' : 'Inactive'}
                          key={`active-${testimonial.id}-${testimonial.is_active}`}
                        />
                      </td>
                      <td>
                        <small className="text-muted">
                          {formatDate(testimonial.created_at)}
                        </small>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          onClick={() => handleEdit(testimonial)}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(testimonial.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            
            {testimonials.length === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-comments fa-3x mb-3"></i>
                <p>No testimonials found. Add your first testimonial!</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Add/Edit Testimonial Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingTestimonial ? 'Edit Testimonial' : 'Add Testimonial'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., John Doe"
                    />
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Designation</Form.Label>
                    <Form.Control
                      type="text"
                      name="designation"
                      value={formData.designation}
                      onChange={handleInputChange}
                      placeholder="e.g., CEO, ABC Company"
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Rating</Form.Label>
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        {renderStars(formData.rating)}
                      </div>
                      <Form.Select
                        name="rating"
                        value={formData.rating}
                        onChange={handleInputChange}
                        style={{ width: '80px' }}
                      >
                        {[1, 2, 3, 4, 5].map(num => (
                          <option key={num} value={num}>
                            {num} star{num !== 1 ? 's' : ''}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Display Order</Form.Label>
                    <Form.Control
                      type="number"
                      name="display_order"
                      value={formData.display_order}
                      onChange={handleInputChange}
                      min="1"
                    />
                    <Form.Text className="text-muted">
                      Lower numbers appear first
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Testimonial Content *</Form.Label>
                <Form.Control
                  as="textarea"
                  name="content"
                  value={formData.content}
                  onChange={handleTextareaChange}
                  rows={5}
                  required
                  placeholder="Share what they said about your product/service..."
                />
                <Form.Text className="text-muted">
                  Enter the testimonial quote
                </Form.Text>
              </Form.Group>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Profile Photo</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      disabled={uploadingImage}
                    />
                    <Form.Text className="text-muted">
                      Upload customer's photo (optional)
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <div>
                      <Form.Check
                        type="switch"
                        name="is_active"
                        label="Active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Inactive testimonials won't be shown
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Image Preview */}
              {(imagePreview || formData.image_url) && (
                <div className="mb-3">
                  <Form.Label>Photo Preview</Form.Label>
                  <div className="d-flex align-items-center">
                    <Image 
                      src={imagePreview?.startsWith('data:') ? imagePreview : `${UPLOAD_BASE_URL}${formData.image_url}`}
                      alt="Preview" 
                      roundedCircle
                      thumbnail 
                      style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                      className="me-3"
                    />
                    
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={removeImage}
                    >
                      <i className="fas fa-trash me-1"></i> Remove Photo
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Preview Card */}
              <Card className="mt-3 border-secondary">
                <Card.Body>
                  <h6>Preview</h6>
                  <div className="d-flex align-items-start">
                    {(imagePreview || formData.image_url) ? (
                      <Image 
                        src={imagePreview?.startsWith('data:') ? imagePreview : `${UPLOAD_BASE_URL}${formData.image_url}`}
                        alt="Preview" 
                        roundedCircle
                        style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                        className="me-3"
                      />
                    ) : (
                      <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center me-3"
                        style={{ width: '60px', height: '60px' }}>
                        <i className="fas fa-user text-white"></i>
                      </div>
                    )}
                    <div>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <strong>{formData.name || 'Customer Name'}</strong>
                          {formData.designation && (
                            <div className="text-muted small">{formData.designation}</div>
                          )}
                        </div>
                        <div className="text-warning">
                          {renderStars(formData.rating)}
                        </div>
                      </div>
                      <div className="mt-2">
                        <small className="text-muted">
                          "{formData.content || 'Testimonial content will appear here...'}"
                        </small>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
              
              {editingTestimonial && (
                <div className="alert alert-secondary mt-3">
                  <small>
                    <i className="fas fa-info-circle me-2"></i>
                    Created: {formatDate(editingTestimonial.created_at)}
                  </small>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploadingImage}>
                {uploadingImage && <Spinner animation="border" size="sm" className="me-2" />}
                {editingTestimonial ? 'Update' : 'Add'} Testimonial
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default TestimonialsManager;